import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search } from 'lucide-react';
import { scoreMatch } from '../search/fuzzySearch';
import { buildIndex, SearchEntry, stripMarkdown } from '../search/searchIndex';
import { useContentVersion } from '../hooks/ContentVersionContext';
import './MobileSearch.css';

// One index per content version; the active one follows the HF / Void toggle.
const searchIndexes = { hf: buildIndex('hf'), void: buildIndex('void') };

export default function MobileSearch() {
  const [version] = useContentVersion();
  const searchIndex = searchIndexes[version];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchEntry[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('mobile-search-open', onOpen);
    return () => window.removeEventListener('mobile-search-open', onOpen);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const found = searchIndex
      .map((item) => ({ ...item, _score: Math.max(scoreMatch(item.title, query) * 2, scoreMatch(item.text, query)) }))
      .filter((item) => item._score! > 0)
      .sort((a, b) => b._score! - a._score!)
      .slice(0, 14);
    setResults(found);
  }, [query, searchIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const go = useCallback((entry: SearchEntry) => {
    navigate(entry.path);
    setOpen(false);
  }, [navigate]);

  if (!open) return null;

  return (
    <div className="mobile-search-overlay" onClick={() => setOpen(false)}>
      <div className="mobile-search-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-search-input-row">
          <Search size={18} className="mobile-search-icon" />
          <input
            ref={inputRef}
            className="mobile-search-input"
            type="text"
            placeholder="Search anything…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          <button className="mobile-search-close" onClick={() => setOpen(false)} aria-label="Close search">
            <X size={18} />
          </button>
        </div>

        {results.length > 0 && (
          <ul className="mobile-search-results">
            {results.map((r, i) => (
              <li key={i}>
                <button className="mobile-search-result" onClick={() => go(r)}>
                  <span className="mobile-search-result__title">{r.title}</span>
                  <span className="mobile-search-result__snippet">
                    {stripMarkdown(r.text).slice(0, 80)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {query.trim() && results.length === 0 && (
          <p className="mobile-search-empty">No results for "{query}"</p>
        )}
      </div>
    </div>
  );
}
